import { createClient } from '@supabase/supabase-js'

/**
 * Supabase Auth in the browser (Phase 1b §3.1).
 *
 * ── THIS IS NOT THE mws-admin PATTERN, DELIBERATELY ─────────────────────────
 * `@supabase/ssr` with cookie-backed server sessions is what the operator's
 * other Next.js projects use, and it is exactly what MBOS must not do. Phase 0
 * §2 is bearer-tokens-only: no cookies, no CSRF token, no server session, the
 * Express API as the single boundary. A cookie here would put a session where
 * the architecture says there is none, and would quietly make the SPA a second
 * backend's client.
 *
 * So: the plain browser client, session in localStorage, and the access token
 * attached by hand to every MBOS API call in lib/api.js.
 *
 * ── WHAT THIS CLIENT IS AND IS NOT FOR ──────────────────────────────────────
 * Authentication ONLY — sign in, sign out, refresh, session restore. It must
 * NEVER be used to read or write application data directly, even though the
 * anon key technically permits it under RLS. Every read and write goes through
 * the MBOS API, because that is where tenant resolution, feature gating, stage
 * history, server-computed totals and the stable error contract live. A query
 * issued straight from the browser bypasses all of it.
 */

const url = import.meta.env.VITE_SUPABASE_URL
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!url || !anonKey) {
  // Fail at boot with the fix, rather than at first sign-in with a stack
  // trace. This is the single most common local-setup mistake.
  throw new Error(
    'Missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY. ' +
    'Copy dashboard/.env.example to dashboard/.env.local and fill it in.'
  )
}

export const supabase = createClient(url, anonKey, {
  auth: {
    // The SPA-appropriate configuration: the session survives a reload, and
    // the library refreshes the access token before it expires.
    persistSession: true,
    autoRefreshToken: true,
    // MBOS has no OAuth redirect flow; leaving this on makes the client parse
    // every URL fragment on load looking for tokens that will never be there.
    detectSessionInUrl: false,
    storageKey: 'mbos.auth'
  }
})

/**
 * The current access token, or null.
 *
 * Read from the live session rather than cached in a module variable, so a
 * token refreshed in the background is picked up by the very next request.
 * Caching it is how a request goes out with a token that expired 40 seconds
 * ago and comes back 401 for no visible reason.
 *
 * @returns {Promise<string|null>}
 */
export async function currentAccessToken () {
  const { data } = await supabase.auth.getSession()
  return data.session?.access_token ?? null
}
